import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Package, Image, LogOut, Flower } from 'lucide-react';
import { Button } from '../ui/button';
import { adminLogout } from '../../services/adminApi';
import { layoutClasses, surfaceClasses } from '../../config/designSystem';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/orders', label: 'Orders', icon: Package },
    { path: '/admin/gallery', label: 'Gallery', icon: Image },
  ];

  return (
    <div className={layoutClasses.adminShell}>
      {/* Desktop Sidebar */}
      <aside className={`fixed left-0 top-0 hidden h-full w-64 border-r lg:flex flex-col ${surfaceClasses.adminSidebar}`}>
        {/* Logo */}
        <div className="p-6 border-b border-stone-800">
          <Link to="/admin/orders" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500/20 to-pink-500/10 flex items-center justify-center">
              <Flower className="w-5 h-5 text-rose-400" />
            </div>
            <div>
              <span className="font-serif text-lg font-semibold text-white">FlowBerry</span>
              <span className="block text-xs text-stone-500">Admin Dashboard</span>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-rose-500/10 text-rose-300'
                    : 'text-stone-400 hover:bg-stone-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-stone-800">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-stone-400 hover:text-white hover:bg-stone-800"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className={`lg:hidden sticky top-0 z-50 border-b ${surfaceClasses.adminSidebar}`}>
        <div className="px-4 py-4 flex items-center justify-between gap-4">
          <Link to="/admin/orders" className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500/20 to-pink-500/10 flex items-center justify-center flex-shrink-0">
              <Flower className="w-5 h-5 text-rose-400" />
            </div>
            <div className="min-w-0">
              <span className="font-serif text-lg font-semibold text-white block truncate">FlowBerry</span>
              <span className="block text-xs text-stone-500">Admin Dashboard</span>
            </div>
          </Link>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-stone-400 hover:text-white hover:bg-stone-800 flex-shrink-0"
            aria-label="Sign out"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
        <nav className="px-4 pb-4 flex gap-2 overflow-x-auto">
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-rose-500/10 text-rose-300'
                    : 'text-stone-400 hover:bg-stone-800 hover:text-white'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </header>

      {/* Main Content */}
      <main className="min-h-screen lg:ml-64">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
